<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { Package, UtensilsCrossed } from 'lucide-vue-next';

const props = defineProps({
  // Image "après" (plat servi à l'assiette)
  plateImage: { type: String, required: true },
  // Image "avant" (sachet sous-vide)
  sousVideImage: { type: String, required: true },
  // Nom du plat pour alt + légende
  dishName: { type: String, default: '' },
  // Durée d'affichage de chaque image (ms)
  intervalMs: { type: Number, default: 6500 },
  // Durée de la transition crossfade (ms)
  fadeMs: { type: Number, default: 1500 },
  // Offset de démarrage pour désynchroniser plusieurs instances (ms)
  startDelayMs: { type: Number, default: 0 },
  // Format aspect ratio
  aspect: { type: String, default: 'portrait' } // 'square' | 'portrait'
});

const showingSousVide = ref(true);
let timer = null;
let startTimeout = null;

function tick() {
  showingSousVide.value = !showingSousVide.value;
}

onMounted(() => {
  startTimeout = setTimeout(() => {
    timer = setInterval(tick, props.intervalMs);
  }, Math.max(0, props.startDelayMs));
});
onUnmounted(() => {
  if (startTimeout) clearTimeout(startTimeout);
  if (timer) clearInterval(timer);
});

const aspectClass = computed(() =>
  props.aspect === 'portrait' ? 'aspect-[3/4]' : 'aspect-square'
);

const fadeStyle = computed(() => ({
  transition: `opacity ${props.fadeMs}ms ease-in-out`
}));

const sousVidePillClass = 'bg-[#0077B6] text-white shadow-[0_4px_14px_rgba(0,119,182,0.4)]';
const platePillClass = 'bg-[#e94591] text-white shadow-[0_4px_14px_rgba(233,69,145,0.4)]';

// Tronque les noms de plats trop longs pour qu'ils tiennent toujours sur 2
// lignes max dans la card portrait. Le -webkit-line-clamp CSS n'est pas fiable
// (Chrome force display:flow-root sur figcaption/div dans certains contextes),
// donc on fait un truncate JS robuste : on garde max 52 caractères + ellipsis.
const displayDishName = computed(() => {
  const name = (props.dishName || '').trim();
  if (name.length <= 52) return name;
  // Coupe au dernier espace avant 50 chars pour ne pas couper en plein mot
  const slice = name.slice(0, 50);
  const lastSpace = slice.lastIndexOf(' ');
  return (lastSpace > 30 ? slice.slice(0, lastSpace) : slice) + '…';
});
</script>

<template>
  <figure class="relative w-full overflow-hidden rounded-3xl bg-gray-100 shadow-lg ring-1 ring-black/5" :class="aspectClass">
    <!-- Image plat (après) -->
    <img :src="plateImage"
         :alt="dishName ? `${dishName} servi à l'assiette` : 'Plat servi'"
         class="absolute inset-0 w-full h-full object-cover"
         :style="[fadeStyle, { opacity: showingSousVide ? 0 : 1 }]"
         loading="lazy"
         decoding="async" />
    <!-- Image sous-vide (avant) -->
    <img :src="sousVideImage"
         :alt="dishName ? `${dishName} livré sous-vide` : 'Plat sous-vide'"
         class="absolute inset-0 w-full h-full object-cover"
         :style="[fadeStyle, { opacity: showingSousVide ? 1 : 0 }]"
         loading="lazy"
         decoding="async" />

    <!-- Gradient bas pour lisibilité du titre -->
    <div class="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/75 via-black/30 to-transparent pointer-events-none"></div>

    <!-- Pastille statut en haut. Crossfade synchro avec les images : on a
         deux pastilles superposées dont l'opacité suit le même flag que les
         images, donc l'apparence change exactement en même temps. -->
    <div class="absolute top-3 left-3 pointer-events-none">
      <span :class="['inline-flex items-center gap-2 backdrop-blur-md rounded-full px-3.5 py-2 text-sm font-extrabold font-baloo whitespace-nowrap', sousVidePillClass]"
            :style="[fadeStyle, { opacity: showingSousVide ? 1 : 0 }]">
        <Package class="w-4 h-4" />
        Livré sous-vide
      </span>
      <span :class="['absolute top-0 left-0 inline-flex items-center gap-2 backdrop-blur-md rounded-full px-3.5 py-2 text-sm font-extrabold font-baloo whitespace-nowrap', platePillClass]"
            :style="[fadeStyle, { opacity: showingSousVide ? 0 : 1 }]">
        <UtensilsCrossed class="w-4 h-4" />
        Prêt à déguster
      </span>
    </div>

    <!-- Nom du plat en bas. On combine 2 garde-fous pour éviter le 3 lignes
         qui débordait : (1) truncate JS à ~50 caractères (displayDishName),
         (2) max-height + overflow hidden en CSS comme dernière sécurité. -->
    <div v-if="dishName"
         class="absolute bottom-0 inset-x-0 px-4 pb-3.5 pt-6 text-white font-baloo font-extrabold text-sm md:text-base leading-snug drop-shadow-md pointer-events-none"
         style="max-height: 5rem; overflow: hidden;">
      {{ displayDishName }}
    </div>
  </figure>
</template>
